// src/lib/omnicine/npcEnvelopes.ts
//
// NPC envelope suggestion helpers (Phase N1/N2)
// - Builds lightweight "range suggestions" for NPC envelope bands based on npc:* markers.
// - Emits suggestions as a window CustomEvent so TimelineShell (or any consumer) can choose to apply them.
// - HARDENED: never throws if timeline state is missing or partial.

type NpcEnvelopeKind = "attention" | "emotion" | "safety" | "overload";

export type NpcEnvelopeSuggestion = {
  id: string;
  kind: NpcEnvelopeKind;
  startSec: number;
  endSec: number;
  score: number; // 0..1 confidence (heuristic)
  reason: string;
};

type TimelineLike = {
  durationSec?: number;
  viewportStartSec?: number;
  viewportEndSec?: number;
  markers?: Array<{ id: string; timeSec: number; label?: string | null }>;
};

const EVENT_NAME = "omnicine:npc:envelopes:suggest";

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function safeNum(n: unknown, fallback: number) {
  return typeof n === "number" && Number.isFinite(n) ? n : fallback;
}

function isNpcMarker(m: { label?: string | null }) {
  const label = (m.label ?? "").toString().trim().toLowerCase();
  return label.startsWith("npc:");
}

function inferKindFromLabel(labelRaw: unknown): NpcEnvelopeKind | null {
  const label = (labelRaw ?? "").toString().toLowerCase();

  // Order matters: "overload" and "safety" should win if present.
  if (label.includes("overload")) return "overload";
  if (label.includes("safety") || label.includes("safe")) return "safety";
  if (label.includes("emotion") || label.includes("fear") || label.includes("stress")) return "emotion";
  if (label.includes("attention") || label.includes("focus") || label.includes("awareness")) return "attention";

  return null;
}

function mkId(kind: string, startSec: number, endSec: number) {
  // Deterministic id (stable across refresh if inputs are stable)
  return `npc_${kind}_${startSec.toFixed(3)}_${endSec.toFixed(3)}`;
}

function mergeCloseBands(
  bands: Array<{ kind: NpcEnvelopeKind; startSec: number; endSec: number; score: number; reason: string }>,
  mergeGapSec: number
) {
  const sorted = [...bands].sort((a, b) => a.startSec - b.startSec);

  const out: typeof sorted = [];
  for (const b of sorted) {
    const last = out[out.length - 1];
    if (!last) {
      out.push({ ...b });
      continue;
    }

    if (last.kind === b.kind && b.startSec <= last.endSec + mergeGapSec) {
      // Merge
      last.endSec = Math.max(last.endSec, b.endSec);
      last.score = Math.max(last.score, b.score);
      last.reason = last.reason.includes("merged") ? last.reason : `${last.reason} (merged)`;
      continue;
    }

    out.push({ ...b });
  }

  return out;
}

/**
 * Build envelope suggestions from the current timeline state.
 * SAFE: returns [] if timeline is missing or insufficient.
 */
export function buildNpcEnvelopeSuggestions(
  timeline: TimelineLike | null | undefined,
  opts?: {
    // half-width around a single marker (seconds)
    halfWindowSec?: number;
    // merge same-kind bands if they are within this gap (seconds)
    mergeGapSec?: number;
    // if true, only return suggestions within the visible viewport
    viewportOnly?: boolean;
  }
): NpcEnvelopeSuggestion[] {
  if (!timeline) return [];

  const durationSec = safeNum(timeline.durationSec, 0);
  if (!(durationSec > 0)) return [];

  const viewportStartSec = safeNum(timeline.viewportStartSec, 0);
  const viewportEndSec = safeNum(timeline.viewportEndSec, Math.min(10, durationSec));

  const halfWindowSec = clamp(safeNum(opts?.halfWindowSec, 1.25), 0.25, 10);
  const mergeGapSec = clamp(safeNum(opts?.mergeGapSec, 0.75), 0, 10);
  const viewportOnly = !!opts?.viewportOnly;

  const markers = Array.isArray(timeline.markers) ? timeline.markers : [];
  const npc = markers
    .filter((m) => m && Number.isFinite(m.timeSec))
    .filter(isNpcMarker)
    .sort((a, b) => a.timeSec - b.timeSec);

  if (npc.length === 0) return [];

  // Build primitive bands from markers (kind inferred from label keywords).
  const primitive = npc
    .map((m) => {
      const kind = inferKindFromLabel(m.label);
      if (!kind) return null;

      const t = clamp(m.timeSec, 0, durationSec);
      const s = clamp(t - halfWindowSec, 0, durationSec);
      const e = clamp(t + halfWindowSec, 0, durationSec);

      // Heuristic confidence: we can tune later.
      const score =
        kind === "overload" ? 0.9 :
        kind === "safety" ? 0.85 :
        kind === "emotion" ? 0.75 :
        0.7;

      return {
        kind,
        startSec: s,
        endSec: e,
        score,
        reason: `npc marker → ${kind}`,
      };
    })
    .filter(Boolean) as Array<{ kind: NpcEnvelopeKind; startSec: number; endSec: number; score: number; reason: string }>;

  if (primitive.length === 0) return [];

  // Merge close bands of the same kind.
  const merged = mergeCloseBands(primitive, mergeGapSec);

  // Optionally trim to viewport to keep UI suggestions tight.
  const filtered = viewportOnly
    ? merged.filter((b) => b.endSec >= viewportStartSec && b.startSec <= viewportEndSec)
    : merged;

  return filtered.map((b) => ({
    id: mkId(b.kind, b.startSec, b.endSec),
    kind: b.kind,
    startSec: b.startSec,
    endSec: b.endSec,
    score: clamp(b.score, 0, 1),
    reason: b.reason,
  }));
}

/**
 * Emit suggestions as a window event.
 * Supports two call forms:
 *   emitNpcEnvelopeSuggestions(state)
 *   emitNpcEnvelopeSuggestions(suggestionsArray)
 */
export function emitNpcEnvelopeSuggestions(
  input: TimelineLike | NpcEnvelopeSuggestion[] | null | undefined,
  meta?: { source?: string }
): NpcEnvelopeSuggestion[] {
  const suggestions = Array.isArray(input)
    ? input
    : buildNpcEnvelopeSuggestions(input, { viewportOnly: true });

  try {
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: {
          suggestions,
          source: meta?.source ?? "npcEnvelopes",
          emittedAt: Date.now(),
        },
      })
    );
  } catch {
    // best-effort, never throw
  }

  return suggestions;
}


